import {
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { isOneOf } from './array.helpers';
import { is } from '../../client/src/helpers/dom.guards';
import {
  hasOpenOverlayInDocument,
  hasOverlayParent,
  isInteractiveFormControlElement,
  isTextInputElement,
} from '../../client/src/helpers/dom.helpers';

export type MouseCallback = (event?: ReactMouseEvent) => unknown;

export interface IEventHandlerOptions {
  /**
   * Stop the event from propagating (IE. reach event listeners on parent elements)
   */
  stopPropagation?: boolean;

  /**
   * Prevent default behavior of the event like showing a context menu
   */
  preventDefault?: boolean;

  /**
   * Ignore input events like clicks on input elements that might happen inside the target element
   */
  ignoreInputEvents?: boolean;

  /**
   * Ignore clicks in modal, popover, tooltip, etc.
   */
  ignoreOverlayEvents?: boolean;

  /**
   * When set with {@link IEventHandlerOptions.ignoreInputEvents} and
   * {@link IEventHandlerOptions.ignoreOverlayEvents} on keyboard events, only
   * block shortcuts for text inputs (not buttons or other form controls).
   * Used by undo/redo so Cmd+Z still works after clicking a menu item or button.
   */
  ignoreOnlyTextInputForKeyboard?: boolean;
}

const INPUT_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'] as const;

export const eventHandler = <
  E extends MouseEvent | ReactMouseEvent | KeyboardEvent | ReactKeyboardEvent,
  R,
>(
  callback?: (e: E) => R,
  options: IEventHandlerOptions = {},
): ((e: E) => R | undefined) => {
  return (e: E): R | undefined => {
    if (e) {
      // Apply defaults
      const {
        stopPropagation = true,
        preventDefault = false,
        ignoreInputEvents = false,
        ignoreOverlayEvents = false,
        ignoreOnlyTextInputForKeyboard = false,
      } = options;

      if (ignoreInputEvents && ignoreOverlayEvents && isKeyboardEvent(e)) {
        const shouldIgnore = ignoreOnlyTextInputForKeyboard
          ? shouldIgnoreUndoRedoShortcut(e)
          : shouldIgnoreKeyboardShortcut(e);
        if (shouldIgnore) return;
      }
      // Keyboard + ignoreOnlyTextInputForKeyboard: text inputs handled above via shouldIgnoreUndoRedoShortcut
      const skipGenericInputCheck =
        ignoreOnlyTextInputForKeyboard && isKeyboardEvent(e);
      if (ignoreInputEvents && isInputEvent(e) && !skipGenericInputCheck) {
        return;
      }
      if (ignoreOverlayEvents && isOverlayEvent(e)) {
        return;
      }
      if (stopPropagation) {
        e.stopPropagation();
      }
      if (preventDefault) {
        e.preventDefault();
      }
      if (callback) {
        return callback(e);
      }
    }
  };
};

export const keyboardEventHandler = <R>(
  callback?: (e: KeyboardEvent) => R,
  options: IEventHandlerOptions = {},
): ((e: KeyboardEvent) => R | undefined) => eventHandler(callback, options);

export const mouseEventHandler = <R>(
  callback?: (e: ReactMouseEvent) => R,
  options: IEventHandlerOptions = {},
): ((e: ReactMouseEvent) => R | undefined) => eventHandler(callback, options);

/**
 * Detect if the user is interacting with an input element.
 * Some deselection/selection logic should be ignored if the user is interacting with an input.
 * @param e
 * @returns
 */
export const isInputEvent = (
  e: MouseEvent | ReactMouseEvent | KeyboardEvent | ReactKeyboardEvent,
): boolean => {
  const target = e?.target as HTMLInputElement | undefined;

  const isSliderInput = target?.closest('.MuiSlider-root') !== null;
  if (isSliderInput) return false; // we ignore mui based sliders.. the main point is to avoid text-based inputs.

  // Don't trigger anything if the user is interacting with an input
  return isOneOf(INPUT_TAGS, target?.tagName) && !target.disabled;
};

/**
 * Check if the event is triggered inside an overlay element.
 * What is considered an overlay element is determined in {@link hasOverlayParent}
 * @param e some event
 * @returns
 */
export const isOverlayEvent = (
  e: MouseEvent | ReactMouseEvent | KeyboardEvent | ReactKeyboardEvent,
): boolean => is.Element(e.target) && hasOverlayParent(e.target);

/**
 * Narrows DOM or React synthetic events to keyboard events.
 */
export const isKeyboardEvent = (
  e: unknown,
): e is KeyboardEvent | ReactKeyboardEvent => {
  if (e instanceof KeyboardEvent) return true;
  if (typeof e !== 'object' || e === null || !('nativeEvent' in e)) {
    return false;
  }
  return (e as ReactKeyboardEvent).nativeEvent instanceof KeyboardEvent;
};

const keyboardEventElements = (
  e: KeyboardEvent | ReactKeyboardEvent,
): Element[] => {
  const elements: Element[] = [];
  if (is.Element(e.target)) elements.push(e.target);
  const active = document.activeElement;
  if (is.Element(active) && !elements.includes(active)) elements.push(active);
  return elements;
};

const isKeyboardInteractionBlockedForElement = (element: Element): boolean => {
  if (isTextInputElement(element)) return true;
  if (isInteractiveFormControlElement(element)) return true;
  if (hasOverlayParent(element)) return true;
  return false;
};

/**
 * When true, undo/redo shortcuts must not run — the user is typing in a text
 * field or a modal/popover/dialog is active. Unlike {@link shouldIgnoreKeyboardShortcut},
 * focused buttons and other non-text form controls do not block undo/redo.
 */
export const shouldIgnoreUndoRedoShortcut = (
  e: MouseEvent | ReactMouseEvent | KeyboardEvent | ReactKeyboardEvent,
): boolean => {
  if (isOverlayEvent(e)) return true;
  if (hasOpenOverlayInDocument()) return true;
  if (!isKeyboardEvent(e)) return false;

  for (const element of keyboardEventElements(e)) {
    if (isTextInputElement(element)) return true;
    if (hasOverlayParent(element)) return true;
  }

  return false;
};

/**
 * When true, document-level list shortcuts must not run — the user is typing,
 * using a form control, or a modal/popover/dialog is active.
 */
export const shouldIgnoreKeyboardShortcut = (
  e: MouseEvent | ReactMouseEvent | KeyboardEvent | ReactKeyboardEvent,
): boolean => {
  if (isInputEvent(e)) return true;
  if (isOverlayEvent(e)) return true;
  if (hasOpenOverlayInDocument()) return true;
  if (!isKeyboardEvent(e)) return false;

  for (const element of keyboardEventElements(e)) {
    if (isKeyboardInteractionBlockedForElement(element)) return true;
  }

  return false;
};
